/* ------------------------ Functional imports start ------------------------ */
import { type Supplier, type SupplierInfo } from './types'
import LogTool from '../../../logger/logTools'
import { apiClient, type PaginatedResponse } from '../../../api/client'
import { buildFetchUrl, createContact } from '../../../utils/functions'
import { type FetchKwargs } from '../../../api/types'
import {
  createSupplier as createSupplierApiUtil,
  createSupplierInfo as createSupplierInfoApiUtil,
  formInputToAPISupplierJSON as formInputToAPISupplierJSONApiUtil,
  createSupplierApi,
  updateSupplierApi,
  deleteSuppliersApi,
} from '../api/supplierApi'
/* ------------------------- Functional imports end ------------------------- */

const log = new LogTool({ context: 'SupplierUtils', enable: true, logLevel: 'warn' })

const createSupplierInternal = createSupplierApiUtil
const createSupplierInfoInternal = createSupplierInfoApiUtil
const formInputToAPISupplierJSONInternal = formInputToAPISupplierJSONApiUtil

/* -------------------------------------------------------------------------- */
/*                           Utility functions start                          */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                             API functions start                            */
/* -------------------------------------------------------------------------- */

/**
 * Fetch supplier data from backend. Note that this fetches all pages of a paginated response sequentially.
 * @param onSuccess Callback that is executed every time when receiving a response from the server.
 * @param onFinal Callback that is executed after calling onSuccess for the last time.
 * @param onError Callback that is executed every time when receiving an error from the server.
 * @param parameter Array of URL parameter to append to the url.
 * @param url The fetch base url.
 * @returns
 */
export async function fetchSuppliers({
  onSuccess,
  onFinal = () => null,
  onError = (error: any) => null,
  parameter = [],
  url = 'v1/supplier/suppliers/',
  suppressWarning = false,
}: FetchKwargs<Supplier> & { suppressWarning?: boolean }): Promise<void> {
  if (!suppressWarning) {
    log.warn(
      'fetchSuppliers is deprecated. Please migrate to useSuppliers() from features/Supplier/hooks/useSupplierQueries.'
    )
  }

  log.info('Begin fetching suppliers.')
  if (!url.match(/v1\/supplier\/suppliers\//) && !url.match(/supplier\/suppliers\//)) {
    log.error(`The provided URL ${url} does not lead to the correct API endpoint!`)
    return
  }

  const fetchUrl = buildFetchUrl(url, parameter)

  try {
    const response = await apiClient.get<
      PaginatedResponse<any> & { results?: any[]; next?: string | null; count?: number }
    >(fetchUrl)

    log.info('Success fetching suppliers for URL', fetchUrl, ', Response ->', response)

    const rawResults = Array.isArray(response?.results) ? response.results : []
    const suppliers: Supplier[] = rawResults.map((supplierResponse: any) =>
      createSupplierInternal(supplierResponse)
    )
    const count =
      typeof response?.count === 'number'
        ? response.count
        : suppliers.length

    if (response?.next) {
      await fetchSuppliers({
        onSuccess,
        onError,
        onFinal,
        parameter,
        url: response.next,
        suppressWarning: true,
      })
    }

    onSuccess(suppliers, count)
    if (!response?.next) {
      onFinal()
    }
  } catch (error) {
    log.error('Error fetching suppliers', error)
    onError(error)
  }
}

export async function fetchSupplier(
  url: string,
  onSuccess: (response: Supplier) => void,
  onError: (error: any) => void,
  parameter: string[] = []
): Promise<void> {
  log.warn(
    'fetchSupplier is deprecated. Please migrate to useSupplier() from features/Supplier/hooks/useSupplierQueries.'
  )
  log.info('Begin fetching supplier.')

  const fetchUrl = url + buildFetchUrl('', parameter)

  try {
    const response = await apiClient.get<any>(fetchUrl)
    log.info('Success fetching supplier for URL', url)
    const supplier = createSupplierInternal(response)
    onSuccess(supplier)
  } catch (error) {
    log.error('Error fetching supplier', error)
    onError(error)
  }
}

export async function postSupplier(
  supplier: Supplier,
  onSuccess: (supplier: Supplier) => void,
  onError: (error: any) => void
): Promise<void> {
  log.warn(
    'postSupplier is deprecated. Please migrate to useCreateSupplier() from features/Supplier/hooks/useSupplierMutations.'
  )
  log.info('Begin posting supplier.')
  if (!supplier) return

  try {
    const createdSupplier = await createSupplierApi(supplier)
    log.info('Successfully posted supplier.')
    onSuccess(createdSupplier)
  } catch (error) {
    log.error('Error posting supplier.', error)
    onError(error)
  }
}

export async function updateSupplier(
  supplier: Supplier,
  onSuccess: (supplier: Supplier) => void,
  onError: (error: any) => void
): Promise<void> {
  log.warn(
    'updateSupplier is deprecated. Please migrate to useUpdateSupplier() from features/Supplier/hooks/useSupplierMutations.'
  )
  log.info('Begin updating supplier.')
  if (!supplier) return
  if (!supplier.self) {
    throw new Error('Cannot update supplier without self.')
  }

  try {
    const updatedSupplier = await updateSupplierApi(supplier.self, supplier)
    log.info('Successfully updated supplier.')
    onSuccess(updatedSupplier)
  } catch (error) {
    log.error('Error updating supplier.', error)
    onError(error)
  }
}

export async function deleteSuppliers(
  suppliers: Supplier[],
  onSuccess: (suppliers: Supplier[]) => void,
  onError: () => void
): Promise<void> {
  log.warn(
    'deleteSuppliers is deprecated. Please migrate to useDeleteSuppliers() from features/Supplier/hooks/useSupplierMutations.'
  )
  log.info('Begin deleting suppliers.')
  if (suppliers.length === 0) return

  try {
    await deleteSuppliersApi(suppliers)
    log.info('Successfully deleted suppliers.')
    onSuccess(suppliers)
  } catch (error) {
    log.error('Error deleting suppliers.', error)
    onError()
  }
}

export { createSupplierInternal as createSupplier }
export { formInputToAPISupplierJSONInternal as formInputToAPISupplierJSON }
